/**
 * Groupes de jeu (NationsGlory : Nova, Iris...) : fonctions pures du front, sans I/O ni store.
 *
 * Un groupe est servi par son propre satellite. Le front choisit le groupe par jeu, l'envoie à la
 * passerelle du panel dans l'en-tête `X-Game-Group`, et la passerelle choisit la cible et signe le
 * groupe dans l'assertion. Un jeu sans groupes (Paladium) n'envoie jamais l'en-tête et n'affiche
 * rien : c'est `supportsGameGroups` (config des jeux) qui garde cette frontière.
 *
 * Un membre du staff peut être affecté à certains groupes seulement (onglet « Grades & accès ») :
 * l'API marque les autres `reason: 'not-member'` dans `project-groups` et les refuse à la passerelle
 * (GAME_GROUP_FORBIDDEN). Le front ne les propose jamais (sélecteur, repli, requêtes à groupe imposé).
 */
import { supportsGameGroups } from '@/config/games';
import { spaceFromPath } from '@/stores/space.store';
import type { ProjectGroupDTO, ProjectGroupsEntryDTO } from '@/services/api.service';

/** En-tête lu par la passerelle du panel (jamais relayé au satellite). */
export const GAME_GROUP_HEADER = 'X-Game-Group';

/** Paramètre d'URL qui porte le groupe d'une page (liens copiés depuis la barre d'adresse). */
export const GAME_GROUP_PARAM = 'g';

/**
 * Événement émis quand la passerelle refuse le groupe envoyé (`code` GAME_GROUP_*) : le layout
 * relit alors les groupes, et le repli choisit un groupe disponible.
 */
export const GAME_GROUP_REFUSED_EVENT = 'recube:game-group-refused';

/**
 * Motif posé par l'API sur un groupe auquel le membre du staff n'est pas affecté : ce groupe n'est
 * jamais proposé (sélecteur, « Créer aussi sur », union des grades protégés).
 */
export const NOT_MEMBER_REASON = 'not-member';

/** Motif lisible d'un groupe hors de l'affectation, pour la notification de repli. */
const NOT_MEMBER_TEXT = "tu n'es pas affecté à ce groupe";

/** Même format que les clés du CICD et de la passerelle. */
const KEY = /^[a-z][a-z0-9-]{0,15}$/;

/** Clé normalisée (espaces retirés, minuscules), ou null si absente ou mal formée. */
export function normalizeGameGroupKey(raw: string | null | undefined): string | null {
  const key = (raw ?? '').trim().toLowerCase();
  return KEY.test(key) ? key : null;
}

/**
 * Pages de Configuration qui lisent le satellite du groupe actif (Mondes). Le catalogue des
 * Cosmétiques des grades vit dans le pack et les containers, communs à tous les groupes : il ne
 * dépend pas du groupe actif.
 */
const GROUP_CONFIG_PAGES = ['/config/worlds'];

/**
 * Pages de Supervision qui ne lisent pas le groupe actif : servies en local par le panel (Logs).
 * Grades in-game suit le groupe choisi dans le sélecteur, comme les autres pages.
 */
const GROUP_FREE_SUPERVISION_PAGES = ['/logs'];

/**
 * Pages de Statistiques qui ne lisent pas le groupe actif : Onboarding interroge toujours le satellite ÉCRIVAIN (le
 * groupe qui annonce la capacité « onboarding », le lobby sur NationsGlory) et a son propre filtre de groupe. Ni
 * sélecteur, ni `?g=`, ni écran « aucun groupe disponible » : la page dit elle-même ce qui manque.
 */
const GROUP_FREE_STATS_PAGES = ['/stats/onboarding'];

/**
 * Pages servies par le panel dont la vue suit quand même le groupe choisi : Grades et accès montre
 * les membres du groupe. Le sélecteur s'y affiche, mais la page ne lit pas le satellite : elle reste
 * ouverte sans groupe disponible (elle montre alors tous les membres que l'API lui renvoie).
 */
const GROUP_VIEW_PAGES = ['/admin'];

function isUnder(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

function trimPath(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

/**
 * La page dépend-elle du groupe ? C'est là, et seulement là, que le sélecteur de groupe s'affiche
 * et que le groupe reste dans l'URL (`?g=`).
 *
 * - Supervision, sauf Logs (journal du panel) ; Grades et accès en fait partie, sa liste suit le groupe ;
 * - Statistiques, sauf Onboarding (satellite écrivain forcé, son propre filtre de groupe) ;
 * - Configuration : Mondes seulement, le reste est servi par le panel (packs, containers) ;
 * - jamais Déploiement (sa propre répartition) ni Paramètres.
 */
export function usesGameGroup(pathname: string): boolean {
  const path = trimPath(pathname);
  switch (spaceFromPath(path)) {
    case 'statistiques':
      return !GROUP_FREE_STATS_PAGES.some((p) => isUnder(path, p));
    case 'configuration':
      return GROUP_CONFIG_PAGES.some((p) => isUnder(path, p));
    case 'supervision':
      return !GROUP_FREE_SUPERVISION_PAGES.some((p) => isUnder(path, p));
    default:
      return false;
  }
}

/**
 * Page dont seule la vue suit le groupe (GROUP_VIEW_PAGES) : le layout ne la remplace jamais par
 * « aucun groupe disponible », puisqu'elle ne lit pas le satellite.
 */
export function groupViewOnly(pathname: string): boolean {
  const path = trimPath(pathname);
  return GROUP_VIEW_PAGES.some((p) => isUnder(path, p));
}

/**
 * Le membre du staff peut-il utiliser ce groupe de ce projet, d'après son affectation enregistrée
 * (`PanelUserDTO.gameGroups`) ? Aucune affectation sur le projet = tous ses groupes. Ne dit rien des
 * admins (global ou du projet), qui ont toujours tous les groupes.
 */
export function assignmentAllowsGroup(
  gameGroups: Record<string, string[]> | null | undefined,
  projectId: string,
  key: string,
): boolean {
  const stored = gameGroups?.[projectId];
  return !Array.isArray(stored) || stored.includes(key);
}

/**
 * Groupe réellement envoyé pour un jeu : le jeu a des groupes, il en avait au dernier chargement
 * réussi, et un groupe est choisi. Si le dernier chargement a échoué, le groupe persisté part
 * quand même : la passerelle reste juge (400 s'il n'existe plus), plutôt qu'une lecture muette de
 * la racine alors que l'utilisateur a choisi Iris.
 */
export function gameGroupHeaderFor(
  gameId: string,
  known: Record<string, boolean>,
  byGame: Record<string, string>,
): string | null {
  if (!supportsGameGroups(gameId) || !known[gameId]) return null;
  return normalizeGameGroupKey(byGame[gameId]);
}

/**
 * En-têtes de contexte d'un appel au backend : le projet, et le groupe quand le jeu en a.
 *
 * @param forced groupe imposé par l'appelant (une requête par groupe), undefined = groupe actif.
 *               Ignoré pour un jeu sans groupes : Paladium ne reçoit jamais `X-Game-Group`.
 */
export function contextHeadersFor(
  gameId: string,
  known: Record<string, boolean>,
  byGame: Record<string, string>,
  forced?: string | null,
): Record<string, string> {
  const headers: Record<string, string> = { 'X-Project': gameId };
  const group = forced !== undefined
    ? (supportsGameGroups(gameId) ? normalizeGameGroupKey(forced) : null)
    : gameGroupHeaderFor(gameId, known, byGame);
  if (group) headers[GAME_GROUP_HEADER] = group;
  return headers;
}

/**
 * Groupes permis sur ce jeu d'après la session (/api/auth/me) : liste des clés, ou null = tous
 * (aucune affectation, admin, session ouverte avant l'affectation). Référence stable (celle de la
 * session), utilisable telle quelle dans un sélecteur de store.
 */
export function allowedGameGroupsOf(
  user: { gameGroups?: Record<string, string[] | null> } | null | undefined,
  gameId: string,
): string[] | null {
  const v = user?.gameGroups?.[gameId];
  return Array.isArray(v) ? v : null;
}

/**
 * Le membre du staff est-il affecté à ce groupe ? Faux si l'API l'a marqué `not-member`, ou s'il
 * est hors de la liste de la session (`allowed`, null ou omis = tous).
 */
export function isMemberGroup(g: ProjectGroupDTO, allowed?: string[] | null): boolean {
  if (g.reason === NOT_MEMBER_REASON) return false;
  return !allowed || allowed.includes(g.key);
}

/** Groupes à montrer au membre du staff : ceux de son affectation, dans l'ordre de l'entrée. */
export function memberGroups(entry: ProjectGroupsEntryDTO | undefined, allowed?: string[] | null): ProjectGroupDTO[] {
  return (entry?.groups ?? []).filter((g) => isMemberGroup(g, allowed));
}

/**
 * Groupes utilisables pour une requête à groupe imposé (« Créer aussi sur », union des grades) :
 * dans l'affectation ET acceptés par la passerelle.
 */
export function usableGroups(entry: ProjectGroupsEntryDTO | undefined, allowed?: string[] | null): ProjectGroupDTO[] {
  return memberGroups(entry, allowed).filter((g) => g.available);
}

/**
 * La session et l'API divergent sur l'affectation (changée pendant la session, ou session ouverte
 * avant l'affectation) : un groupe marqué `not-member` que la session permet, ou l'inverse. Le
 * layout relit alors /api/auth/me. Faux sans entrée, et face à une API qui ne marque rien. Une cible
 * retirée du CICD est ignorée : la session ne liste que les groupes existants.
 */
export function membershipOutdated(entry: ProjectGroupsEntryDTO | undefined, allowed: string[] | null): boolean {
  if (!entry) return false;
  return entry.groups.some((g) => !g.retired
    && (g.reason === NOT_MEMBER_REASON) === (!allowed || allowed.includes(g.key)));
}

/** Le jeu a des groupes, mais aucun n'est dans l'affectation du membre du staff. */
export function noMemberGroup(entry: ProjectGroupsEntryDTO | undefined, allowed?: string[] | null): boolean {
  return !!entry && entry.groups.length > 0 && memberGroups(entry, allowed).length === 0;
}

/**
 * Groupes de l'entrée qu'on peut affecter à un membre du staff : tous ceux du catalogue (y compris
 * ceux de l'appelant marqués `not-member`), sauf une cible dont le groupe a quitté le CICD.
 */
export function assignableGroups(entry: ProjectGroupsEntryDTO | undefined): ProjectGroupDTO[] {
  return (entry?.groups ?? []).filter((g) => !g.retired);
}

/** Groupe écarté par le repli, pour la notification « Iris indisponible (motif) : Nova sélectionné ». */
export interface DroppedGroup {
  key: string;
  label: string;
  reason: string;
}

export interface GroupChoice {
  /** Groupe retenu, null si aucun n'est disponible (pages satellite sans requête). */
  key: string | null;
  /** Groupe demandé (URL) ou persisté qui n'a pas pu être gardé, null sinon. */
  dropped: DroppedGroup | null;
}

/**
 * Groupe effectif d'un jeu, dans l'ordre :
 *   1. le groupe demandé par l'URL (`?g=`, à l'ouverture seulement), s'il est disponible ;
 *   2. le groupe persisté, s'il est disponible ;
 *   3. la racine, si elle l'est ;
 *   4. le premier groupe disponible ;
 *   5. sinon aucun.
 * « Disponible » = accepté par la passerelle ET dans l'affectation du membre du staff (`allowed`,
 * null ou omis = tous ; l'API marque déjà les autres `not-member`).
 * La santé n'entre jamais dans ce choix : un Iris injoignable reste choisi, ses pages le disent.
 */
export function effectiveGroup(
  entry: ProjectGroupsEntryDTO,
  persisted: string | null | undefined,
  requested?: string | null,
  allowed?: string[] | null,
): GroupChoice {
  const find = (key: string | null | undefined): ProjectGroupDTO | undefined =>
    key ? entry.groups.find((g) => g.key === key) : undefined;
  const wantedUrl = normalizeGameGroupKey(requested);
  const wantedStore = normalizeGameGroupKey(persisted);
  const candidates = [wantedUrl, wantedStore, entry.root, ...entry.groups.map((g) => g.key)];

  let key: string | null = null;
  for (const candidate of candidates) {
    const g = find(candidate);
    if (g?.available && isMemberGroup(g, allowed)) { key = g.key; break; }
  }

  const wanted = wantedUrl ?? wantedStore;
  let dropped: DroppedGroup | null = null;
  if (wanted && wanted !== key) {
    const g = find(wanted);
    let reason = "ce groupe n'existe pas pour ce projet";
    if (g) reason = isMemberGroup(g, allowed) ? (g.reason ?? 'indisponible') : NOT_MEMBER_TEXT;
    dropped = { key: wanted, label: g?.label ?? wanted, reason };
  }
  return { key, dropped };
}

/** État lu par la synchronisation du groupe dans AdminLayout (un jeu à groupes, une page). */
export interface GroupSyncState {
  /** La page lit le jeu (usesGameGroup). */
  groupPage: boolean;
  /** `?g=` déjà lu pour ce jeu (il ne l'est qu'à l'ouverture). */
  urlRead: boolean;
  entry: ProjectGroupsEntryDTO | undefined;
  status: 'idle' | 'loading' | 'loaded' | 'error';
  /** Le jeu avait des groupes au dernier chargement réussi. */
  known: boolean;
  persisted: string | null;
  /** `?g=` normalisé. */
  urlGroup: string | null;
  /** Groupe réellement envoyé (gameGroupHeaderFor). */
  headerGroup: string | null;
  /** Groupes de l'affectation du membre du staff (session), null ou omis = tous. */
  allowed?: string[] | null;
}

/** Ce que la synchronisation doit faire : les champs absents ne changent rien. */
export interface GroupSyncPlan {
  /** Retenir que `?g=` a été lu pour ce jeu. */
  markUrlRead: boolean;
  /** Nouveau groupe persisté (null = l'oublier) ; undefined = inchangé. */
  setGroup?: string | null;
  /** Groupe écarté à notifier. */
  dropped: DroppedGroup | null;
  /** Groupe à écrire dans `?g=` (remplacement, sans entrée d'historique). */
  writeUrl?: string;
}

/**
 * Synchronisation du groupe d'un jeu à groupes, sans I/O (AdminLayout l'applique) :
 *   1. à l'ouverture, sur une page qui lit le jeu, `?g=` est lu une fois, dès que les groupes sont
 *      connus ; s'ils sont illisibles, le lien est suivi tel quel (la passerelle reste juge) ;
 *   2. le groupe effectif remplace le persisté s'il diffère (repli notifié) ;
 *   3. sinon `?g=` est aligné sur le groupe réellement envoyé.
 * Un changement de groupe s'applique seul : l'URL suit au passage suivant.
 */
export function planGroupSync(s: GroupSyncState): GroupSyncPlan {
  const urlPending = s.groupPage && !s.urlRead;
  let markUrlRead = false;
  if (urlPending) {
    if (s.entry) {
      markUrlRead = true;
    } else if (s.status === 'error' && s.known) {
      if (s.urlGroup && s.urlGroup !== s.persisted) return { markUrlRead: true, setGroup: s.urlGroup, dropped: null };
      markUrlRead = true;
    } else if (s.status === 'loaded' || s.status === 'error') {
      markUrlRead = true; // jeu sans groupes déclarés : rien à lire
    } else {
      return { markUrlRead: false, dropped: null }; // groupes pas encore lus : l'URL attend
    }
  }

  let dropped: DroppedGroup | null = null;
  if (s.entry) {
    const choice = effectiveGroup(s.entry, s.persisted, urlPending ? s.urlGroup : null, s.allowed);
    dropped = choice.dropped;
    if (choice.key !== s.persisted) return { markUrlRead, setGroup: choice.key, dropped };
  }
  if (s.groupPage && s.headerGroup && s.urlGroup !== s.headerGroup) {
    return { markUrlRead, dropped, writeUrl: s.headerGroup };
  }
  return { markUrlRead, dropped };
}

/** Texte de la notification de repli. */
export function fallbackMessage(dropped: DroppedGroup, chosen: ProjectGroupDTO | undefined, projectName: string): string {
  const reason = dropped.reason.replace(/[.\s]+$/, '');
  return chosen
    ? `${dropped.label} indisponible (${reason}) : ${chosen.label} sélectionné.`
    : `${dropped.label} indisponible (${reason}) : aucun groupe disponible pour ${projectName}.`;
}

/**
 * Capacités annoncées par le satellite d'un groupe (GET /api/_meta, recopiées dans project-groups),
 * ou null si elles sont inconnues : aucun groupe (jeu sans groupes, aucun choisi), groupes pas encore
 * lus, groupe absent de l'entrée, ou satellite jamais sondé (liste vide, même convention que
 * worldsMissingOn). Inconnues = ne rien masquer. Une cible tombée garde celles de sa dernière sonde.
 */
export function groupCapabilities(entry: ProjectGroupsEntryDTO | undefined, key: string | null): string[] | null {
  if (!entry || !key) return null;
  const caps = entry.groups.find((g) => g.key === key)?.capabilities;
  return caps && caps.length > 0 ? caps : null;
}

/**
 * Groupe « grades seuls » : son satellite sert les grades LuckPerms (ranks) mais pas les joueurs
 * (players), comme un lobby sans base. La fiche d'un joueur, lue dans un autre groupe, y gère ses
 * grades. Capacités inconnues : non.
 */
export function isRanksOnlyGroup(g: ProjectGroupDTO): boolean {
  const caps = g.capabilities ?? [];
  return caps.includes('ranks') && !caps.includes('players');
}

/**
 * Groupes « grades seuls » de l'affectation du membre du staff, autres que le groupe actif, dans
 * l'ordre de l'entrée : un cadre de grades chacun sur la fiche joueur. Un groupe refusé par la
 * passerelle reste listé (son cadre donne le motif), une cible retirée du CICD non.
 */
export function ranksOnlyGroups(
  entry: ProjectGroupsEntryDTO | undefined,
  allowed: string[] | null | undefined,
  activeKey: string | null,
): ProjectGroupDTO[] {
  return memberGroups(entry, allowed).filter((g) => !g.retired && g.key !== activeKey && isRanksOnlyGroup(g));
}

/** Libellé et couleur d'un groupe ; sans entrée chargée (lecture en échec), la clé sert de libellé. */
export interface GameGroupLabel {
  key: string;
  label: string;
  color?: string;
}

export function describeGameGroup(entry: ProjectGroupsEntryDTO | undefined, key: string): GameGroupLabel {
  const g = entry?.groups.find((x) => x.key === key);
  return g ? { key, label: g.label, color: g.color } : { key, label: key };
}

/** « sur Iris » pour les confirmations, rien sans groupe (texte d'avant pour Paladium). */
export function onGameGroup(group: GameGroupLabel | null | undefined): string {
  return group ? ` sur ${group.label}` : '';
}
